// /* eslint-disable no-nested-ternary */
import { useEffect, useState, useRef } from 'react';
import ReactPlayer from 'react-player';

import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { IconButton, LinearProgress } from '@mui/material';

import PropTypes from 'prop-types';
import _ from 'lodash';
import { FadeInAnimation, Image } from '../shared';
import useResponsive from '../../../utils/responsive';
import Carousel from '../carousel/carousel.component';

const GalleryCarousel = (props) => { 
    
    const { data, category, inDialog, selectedMedia, selectedIndex } = props;

    const carouselRef = useRef();

    const { isMobile } = useResponsive();

    const [currentSlide, setCurrentSlide] = useState(0);

    const [currentSlideNumber, setCurrentSlideNumber] = useState(1);

    const totalSlides = _.size(data);

    const scaledValue = 100 / totalSlides;    

    let playing = false;

    useEffect(() => {
        determineCurrentSlide();
    }, []);

    const determineCurrentSlide = () => {
        if (selectedMedia) {
             let currentMediaIndex = -1;
            if (selectedMedia.type === 'image') {
                currentMediaIndex = _.findIndex(data, {
                    media_items_image: selectedMedia.media,
                });                
            } else if (selectedMedia.type === 'video') {
                currentMediaIndex = _.findIndex(data, {
                    media_items_video_src: selectedMedia.media,
                });                
            }            
            if (currentMediaIndex !== -1) {
                setCurrentSlide(currentMediaIndex); 
                setCurrentSlideNumber(currentMediaIndex + 1);
            }                        
        }else  {
            if (selectedIndex !== undefined && selectedIndex >= 0) {
                setCurrentSlide(selectedIndex); 
                setCurrentSlideNumber(selectedIndex + 1);
            }  
        }
       
    };    

    const responsive = {
        desktop: {
            breakpoint: { 
                max: 3000, 
                min: 1024 
            },
            items: 4,
        },
        mobile: {
            breakpoint: {
                max: 464,
                min: 0
            },
            items: 1,
        },
        tablet: {
            breakpoint: {
                max: 1024,
                min: 464
            },
            items: 2,
        }
    };

    const ButtonGroup = ({ currentSlide, totalSlides, setCurrentSlide, carouselRef }) => {

        const goToPrev = () => {
            const newSlide = currentSlide > 0 ? currentSlide - 1 : totalSlides;
            setCurrentSlide(newSlide);
            const number = currentSlideNumber > 1 ? currentSlideNumber - 1 : 1;
            setCurrentSlideNumber(number);
        };

        const goToNext = () => {
            const newSlide = currentSlide !== totalSlides ? currentSlide + 1 : 1;
            setCurrentSlide(newSlide);
            const number = currentSlideNumber < totalSlides ? currentSlideNumber + 1 : 1;
            setCurrentSlideNumber(number);
        };

        return (
            <div className="gallery-carousel-button-group">
                <IconButton
                    id="gallery-carousel-back-button"
                    className="play-page-gallery-carousel-back-button"
                    aria-label="Back"
                    onClick={goToPrev}
                    disabled={currentSlideNumber === 1}
                >
                    <ArrowBack className="play-page-gallery-carousel-arrow-back-button" />
                </IconButton>

                <IconButton
                    id="gallery-carousel-next-button"
                    className="play-page-gallery-carousel-next-button"
                    aria-label="Next"
                    onClick={goToNext}
                    disabled={currentSlideNumber === totalSlides}
                >
                    <ArrowForward className="play-page-gallery-carousel-arrow-next-button" />
                </IconButton>
            </div>
        );
    }

    const renderCarouselFooter = () => (

        <div id="play-page-gallery-carousel-footer" className={`main-grid-container main-gird-spacing-xs-1 main-grid-align-items-xs-center play-page-gallery-carousel-footer-grid ${inDialog ? 'play-page-gallery-carousel-footer-grid-dialog' : 'play-page-gallery-carousel-footer-grid-not-dialog'}`}>

            <div className="main-grid-item main-grid-xs-1">
                <h6 className="play-page-gallery-carousel-slide-counter">
                    {currentSlideNumber}/{totalSlides}
                </h6>
            </div>

            {!isMobile && (
                <div className='main-grid-item main-grid-xs-8'>
                    <LinearProgress
                        className="play-page-gallery-carousel-linear-progress"
                        variant="determinate"
                        value={currentSlide * scaledValue}
                    />
                </div>    
            )}           

            <div className="main-grid-item main-grid-xs-3">
                <ButtonGroup
                    currentSlide={currentSlide}
                    totalSlides={totalSlides}
                    setCurrentSlide={setCurrentSlide}
                    carouselRef={carouselRef}
                />
            </div>

        </div>
    );

    const renderHeader = () => {
        if (inDialog) {
            return (
                <h6 className='play-page-gallery-carousel-sort-by-text'>
                    Sort By{' '}
                    <span>
                        {category && category !== 'All Topics'
                            ? _.capitalize(category)
                            : 'All Topics'}
                    </span>
                </h6>
            );
        }
        return <h3>Our Gallery</h3>;
    };    

    const renderMedia = (image, video, caption) => {

        if (image) {

            return (
                <div className='play-page-gallery-carousel-media-container'>
                    <Image
                        src={image}
                        alt={caption || 'gallery-item'}
                        width="100%"
                        height="80%"
                        id={`${image}-carousel-button`}
                        isContainerButton
                        isOldImage
                        className='play-page-gallery-carousel-image'
                    />
                </div>
            );
        }

        if (selectedMedia) {
            if (selectedMedia.media === video) {
                playing = true;
            } else {
                playing = false;
            }
        }

        return (
            <div className='play-page-gallery-carousel-media-container'>
                <ReactPlayer url={video} width="100%" height="80%" playing={playing} controls />
            </div>
        );

    };    

    const renderCarouselItem = () => {
        return _.map(data, (item, index) => {
            const image = _.get(item, 'media_items_image.url');
            const caption = _.get(item, 'media_items_caption');
            const tag = _.get(item, 'medai_items_tag');
            const video = _.get(item, 'media_items_video_src');
            return (
                <div className={inDialog ? 'play-page-gallery-carousel-inner' : 'play-page-gallery-carousel-inner-not-dialog'} key={index}>
                    {renderMedia(image, video, caption)}
                    <p className='play-page-gallery-carousel-tag'>
                        {_.toUpper(tag)}
                    </p>
                </div>
            );
        });
    };

    const renderCarousel = () => {
        return (
            <>
                
                <div className="play-page-gallery-carousel-header-container">
                    <FadeInAnimation>{renderHeader()}</FadeInAnimation>
                </div>

                <div id="play-page-gallery-carousel-container" className="play-page-gallery-carousel-container">
                    <FadeInAnimation>
                        <Carousel
                            ref={carouselRef}
                            responsive={responsive}
                            showDots={false}
                            slidesToSlide={1}
                            arrows={false}                            
                            customCarouselItemWidth={763.22}
                            externalCurrentSlide={currentSlide}
                        >
                            {renderCarouselItem()}
                        </Carousel>
                        {renderCarouselFooter()}
                    </FadeInAnimation>
                </div>

            </>
        );
    }

    return renderCarousel();

};

export default GalleryCarousel;

GalleryCarousel.propTypes = {
    data: PropTypes.array,
    title: PropTypes.string,
    inDialog: PropTypes.bool,
    selectedMedia: PropTypes.object,
    selectedIndex: PropTypes.number,
};

